import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"

import { NewTooltip } from "~/components/ui"
import { brochureState } from "~/containers/Proposal/state/brochure/brochure-state"
import { proposalLocalState } from "~/containers/Proposal/state/proposal/proposal-state"
import {
  calculatedUtilityRateState,
  useEffectiveUtilityRate,
} from "~/containers/Proposal/state/utility-costs-and-savings/utility-costs-and-savings-state"
import { defaultTextInputStyles } from "~/ui/lib/styles/textInput"
import { Wrapper } from "~/ui/lib/utils/Wrapper"

import { ReactComponent as ResetIcon } from "@svg/reset.svg"

interface UtilityRateEditorProps {
  className?: string
  canEdit?: boolean
}

const UTILITY_RATE_INPUT_ID = `utilityRateEditor`

export const UtilityRateEditor: React.FC<UtilityRateEditorProps> = ({
  className,
  canEdit = true,
}) => {
  const { t } = useTranslation()
  const [, setUtilityRate] = useRecoilState(calculatedUtilityRateState)
  const proposal = useRecoilValue(proposalLocalState)
  const { baseGenabilityUtilityRate } = useRecoilValue(brochureState)
  const resetUtilityRate = useResetRecoilState(calculatedUtilityRateState)
  const { effectiveUtilityRate, hasUserOverride, hasBaseline } =
    useEffectiveUtilityRate()

  const hasContractedRates = Boolean(proposal.utilityRate?.hasContractedRates)
  const isDisabled = !canEdit || hasContractedRates

  const displayValue = useMemo(() => {
    if (effectiveUtilityRate === null || effectiveUtilityRate === undefined) {
      return ``
    }

    if (Number.isNaN(Number(effectiveUtilityRate))) {
      return ``
    }

    return Number(effectiveUtilityRate).toFixed(2)
  }, [effectiveUtilityRate])

  const [inputValue, setInputValue] = useState<string>(displayValue)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!isEditing) {
      setInputValue(displayValue)
    }
  }, [displayValue, isEditing])

  const commitValue = (value: string): number | null => {
    const numeric = Number.parseFloat(value)
    if (Number.isNaN(numeric) || numeric < 0) {
      return null
    }

    const formatted = Number(numeric.toFixed(2))

    // Reset to baseline if user enters the exact baseline value
    if (
      hasBaseline &&
      Number(baseGenabilityUtilityRate.toFixed(2)) === formatted
    ) {
      resetUtilityRate()
      return formatted
    }

    setUtilityRate(formatted)
    return formatted
  }

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target
    setInputValue(value)

    // Commit valid values while typing so the proposal diff updates before
    // blur. Without this the footer Save button stays disabled (it keys off
    // the diff) and clicking it does nothing — a disabled button doesn't
    // steal focus, so the input never blurs and the value never commits.
    commitValue(value)
  }

  const handleBlur = () => {
    setIsEditing(false)

    if (inputValue === ``) {
      setInputValue(displayValue)
      return
    }

    const formatted = commitValue(inputValue)
    setInputValue(formatted === null ? displayValue : formatted.toFixed(2))
  }

  const containerClassName = [`flex flex-col gap-1`, className]
    .filter(Boolean)
    .join(` `)

  return (
    <div className={containerClassName}>
      <NewTooltip
        disabled={!hasContractedRates}
        message={`The selected utility uses contracted rates. They can be edited in the 'Utility Company and Rate' menu.`}
        wrapperStyle={{ display: `flex`, flexDirection: `column`, gap: `6px` }}
      >
        <Wrapper css={defaultTextInputStyles}>
          <div data-role="label-wrapper" className="flex items-center gap-2">
            <label htmlFor={UTILITY_RATE_INPUT_ID}>
              {t(`utilityRate`)} ($/kWh)
            </label>
            {hasUserOverride ? (
              <button
                type="button"
                aria-label="Refresh utility rate"
                title="Refresh utility rate"
                onClick={() => {
                  resetUtilityRate()
                }}
                disabled={isDisabled}
                className="rounded p-1 text-gray-500 transition-colors hover:text-blue-600 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-gray-300"
              >
                <ResetIcon className="h-4 w-4 stroke-current" />
              </button>
            ) : null}
          </div>
          <div data-role="validation" data-valid="true">
            <input
              id={UTILITY_RATE_INPUT_ID}
              name={UTILITY_RATE_INPUT_ID}
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={inputValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={() => setIsEditing(true)}
              disabled={isDisabled}
              placeholder="0.00"
            />
          </div>
        </Wrapper>
      </NewTooltip>
      {hasUserOverride && !isDisabled ? (
        <span className="-mt-3 text-sm">Custom rate utilized</span>
      ) : null}
    </div>
  )
}
